import { useEffect, useState } from "react";
import { api, API, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Kpi } from "../components/Kpi";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Download } from "lucide-react";

const KPIS = ["calls","connects","qualified","followups","cp_meetings","obm","ibm","cp_enrolment","fresh_visits","revisits","total_visits","bookings"];
const PERIODS = ["daily","weekly","monthly","quarterly","fy","custom"];
const ENTITY_TYPES = ["employee","project","team","department","division","role","company"];

export default function Targets() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [all, setAll] = useState(null);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entity_type: "employee", entity_id: "", kpi_type: "bookings", period: "monthly", period_start: "", period_end: "", value: 5 });
  const isAdmin = ["super_admin","partner","management","business_head","site_head","sales_manager","mis","hr"].includes(user?.role);
  const canConfigure = ["super_admin","partner","management","business_head","hr"].includes(user?.role);

  const load = () => {
    api.get("/performance/me").then(r => setMe(r.data));
    if (isAdmin) api.get("/performance/all").then(r => setAll(r.data)).catch(() => {});
    if (canConfigure) api.get("/targets-config/achievement").then(r => setRows(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/projects").then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    try { await api.post("/targets-config", { ...form, value: +form.value }); toast.success("Target created"); setOpen(false); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => { try { await api.delete(`/targets-config/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };

  const exportFull = async () => {
    const res = await fetch(`${API}/export/full.xlsx`, { credentials: "include" });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "gram_full_export.xlsx"; a.click();
    URL.revokeObjectURL(url); toast.success("Downloaded");
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Performance</div>
          <h1 className="font-serif-display text-3xl">Targets vs Achievement</h1>
          <p className="text-sm text-muted-foreground mt-1">Personal performance for every user. Managers see full company view and can configure targets.</p>
        </div>
        {["super_admin","partner","management","mis"].includes(user?.role) && (
          <Button variant="outline" onClick={exportFull} data-testid="export-full-btn"><Download size={14} className="mr-1"/> Full XLSX Export</Button>
        )}
      </div>

      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine" data-testid="perf-mine-tab">My Performance</TabsTrigger>
          {isAdmin && <TabsTrigger value="all" data-testid="perf-all-tab">All Users & Departments</TabsTrigger>}
          {canConfigure && <TabsTrigger value="config" data-testid="perf-config-tab">Target Configuration</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine">
          {me && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Kpi label="Monthly Target" value={me.monthly_target || "-"} sub="Units / Bookings"/>
              <Kpi label="Bookings" value={me.achievement.bookings} sub={me.monthly_target ? `${Math.round((me.achievement.bookings / me.monthly_target) * 100)}% of target` : "no target"}/>
              <Kpi label="Leads Assigned" value={me.achievement.total_leads} sub={`${me.achievement.qualified} qualified`}/>
              <Kpi label="Site Visits" value={me.achievement.visits} sub={`${me.achievement.visits_completed} completed`}/>
              <Kpi label="Calls" value={me.achievement.calls} sub={`${me.achievement.connects} connects`}/>
            </div>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="all">
            {all && (
              <div className="space-y-6">
                <Card className="overflow-hidden">
                  <div className="p-4 text-[11px] uppercase tracking-widest text-muted-foreground">Department Roll-up</div>
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr><th className="text-left px-4 py-3">Department</th><th className="text-right px-4 py-3">Users</th><th className="text-right px-4 py-3">Leads</th><th className="text-right px-4 py-3">Calls</th><th className="text-right px-4 py-3">Visits</th><th className="text-right px-4 py-3">Bookings</th></tr>
                    </thead>
                    <tbody>
                      {all.departments.map(d => (
                        <tr key={d.department} className="border-t border-border">
                          <td className="px-4 py-3 capitalize">{d.department}</td>
                          <td className="px-4 py-3 text-right metric-value">{d.users}</td>
                          <td className="px-4 py-3 text-right metric-value">{d.total_leads}</td>
                          <td className="px-4 py-3 text-right metric-value">{d.calls}</td>
                          <td className="px-4 py-3 text-right metric-value">{d.visits}</td>
                          <td className="px-4 py-3 text-right metric-value">{d.bookings}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
                <Card className="overflow-hidden">
                  <div className="p-4 text-[11px] uppercase tracking-widest text-muted-foreground">Every User</div>
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr><th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Division</th><th className="text-right px-4 py-3">Target</th><th className="text-right px-4 py-3">Bookings</th><th className="text-right px-4 py-3">Ach %</th><th className="text-right px-4 py-3">Leads</th><th className="text-right px-4 py-3">Visits</th><th className="text-right px-4 py-3">Calls</th></tr>
                    </thead>
                    <tbody>
                      {all.users.map(u => (
                        <tr key={u.id} className="border-t border-border hover:bg-accent/5" data-testid={`perf-row-${u.email}`}>
                          <td className="px-4 py-3"><div>{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                          <td className="px-4 py-3 capitalize text-xs">{(u.role || "").replace(/_/g, " ")}</td>
                          <td className="px-4 py-3 text-xs">{u.division}</td>
                          <td className="px-4 py-3 text-right metric-value">{u.monthly_target || "-"}</td>
                          <td className="px-4 py-3 text-right metric-value">{u.achievement.bookings}</td>
                          <td className="px-4 py-3 text-right">{u.monthly_target ? <span className={`metric-value ${u.achievement_pct >= 100 ? "text-emerald-700" : u.achievement_pct >= 60 ? "text-amber-700" : "text-rose-700"}`}>{u.achievement_pct}%</span> : <span className="text-muted-foreground text-xs">n/a</span>}</td>
                          <td className="px-4 py-3 text-right metric-value">{u.achievement.total_leads}</td>
                          <td className="px-4 py-3 text-right metric-value">{u.achievement.visits}</td>
                          <td className="px-4 py-3 text-right metric-value">{u.achievement.calls}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </div>
            )}
          </TabsContent>
        )}

        {canConfigure && (
          <TabsContent value="config">
            <div className="mb-4 flex justify-end">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button data-testid="new-target-btn"><Plus size={14} className="mr-1"/> New Target</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Assign Target</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Entity Type</Label>
                      <Select value={form.entity_type} onValueChange={v => setForm({ ...form, entity_type: v })}>
                        <SelectTrigger data-testid="tgt-entity-type"><SelectValue/></SelectTrigger>
                        <SelectContent>{ENTITY_TYPES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{form.entity_type === "employee" ? "Employee" : form.entity_type === "project" ? "Project" : "Entity ID / Name"}</Label>
                      {form.entity_type === "employee" ? (
                        <Select value={form.entity_id} onValueChange={v => setForm({ ...form, entity_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                          <SelectContent className="max-h-64 overflow-auto">{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : form.entity_type === "project" ? (
                        <Select value={form.entity_id} onValueChange={v => setForm({ ...form, entity_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                          <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input value={form.entity_id} onChange={e => setForm({ ...form, entity_id: e.target.value })}/>
                      )}
                    </div>
                    <div>
                      <Label>KPI</Label>
                      <Select value={form.kpi_type} onValueChange={v => setForm({ ...form, kpi_type: v })}>
                        <SelectTrigger><SelectValue/></SelectTrigger>
                        <SelectContent>{KPIS.map(x => <SelectItem key={x} value={x}>{x.replace(/_/g," ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Period</Label>
                      <Select value={form.period} onValueChange={v => setForm({ ...form, period: v })}>
                        <SelectTrigger><SelectValue/></SelectTrigger>
                        <SelectContent>{PERIODS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Start</Label><Input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })}/></div>
                    <div><Label>End</Label><Input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })}/></div>
                    <div><Label>Target Value</Label><Input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}/></div>
                  </div>
                  <DialogFooter><Button onClick={submit} data-testid="tgt-submit">Create Target</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left px-4 py-3">Entity</th><th className="text-left px-4 py-3">KPI</th><th className="text-left px-4 py-3">Period</th><th className="text-right px-4 py-3">Target</th><th className="text-right px-4 py-3">Actual</th><th className="text-right px-4 py-3">Ach %</th><th className="text-right px-4 py-3">Remaining</th><th className="text-right px-4 py-3">Actions</th></tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-4 py-3"><div className="capitalize text-xs text-muted-foreground">{r.entity_type}</div>{r.entity_name}</td>
                      <td className="px-4 py-3">{r.kpi_type.replace(/_/g," ")}</td>
                      <td className="px-4 py-3 text-xs">{r.period_start} → {r.period_end}</td>
                      <td className="px-4 py-3 text-right metric-value">{r.value}</td>
                      <td className="px-4 py-3 text-right metric-value">{r.actual}</td>
                      <td className="px-4 py-3 text-right"><span className={`metric-value ${r.achievement_pct >= 100 ? "text-emerald-700" : r.achievement_pct >= 60 ? "text-amber-700" : "text-rose-700"}`}>{r.achievement_pct}%</span></td>
                      <td className="px-4 py-3 text-right metric-value">{r.remaining}</td>
                      <td className="px-4 py-3 text-right">
                        {["super_admin","partner"].includes(user.role) && <Button variant="ghost" size="sm" onClick={() => del(r.id)}><Trash2 size={12}/></Button>}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-muted-foreground">No targets configured yet.</td></tr>}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
